import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store/store';
import BoxDraftText from '../common/BoxDraftText';
import BoxActionsPanel from '../common/BoxActionsPanel';
import BoxFormControlsPortal from '../RightSide/BoxFormControlsPortal';
import AccordionPanelFields from '../common/boxStyleFields/AccordionPanelFields';
import BoxStyleTemplatePicker from '../common/boxStyleFields/BoxStyleTemplatePicker';
import { useBoxPropsEditor } from '../common/boxStyleFields/useBoxPropsEditor';
import { mergeConfigurationAccordionPanelStyles } from '../../utils/styles';
import { boxPropsFromStyle } from '../../shared/boxes/styleTemplate';
import { accordionPanelStyles } from '../../shared/boxes/accordionPanel/accordionPanelRender';

/**
 * Box « Volet » du document — la jumelle du bloc web `AccordionPanel`.
 *
 * Elle est TOUJOURS dépliée : un PDF ne se replie pas, et le document doit
 * montrer tout ce qu'il contient. Le repli n'existe que sur le site, où le
 * parser envoie ce même volet
 * (docs/webviewer/chantier-boxes-partagees.md).
 *
 * La box porte SES valeurs (`content.panel`) : un style du template n'est
 * qu'un **modèle**, recopié ici quand on l'applique, et tout se personnalise
 * ensuite dans le panel de droite.
 */
const selector = (box) => ({ updateBox, selectedBoxId, configuration, tmp }) => ({
  updateBox,
  isSelected: selectedBoxId === box.id,
  templates: mergeConfigurationAccordionPanelStyles(configuration, tmp),
});

/** Aperçu d'un modèle : le volet en réduction, avec un texte d'exemple. */
const PanelPreview = ({ style }) => {
  const preview = accordionPanelStyles(style);
  return (
    <div style={{ width: 320, ...preview.frame }}>
      <div style={{
        padding: '12px 16px',
        fontWeight: 600,
        ...preview.header,
        ...(style.questionColor ? { color: style.questionColor } : {}),
      }}>
        Quels sont vos délais ?
      </div>
      <div style={{
        padding: '12px 16px',
        ...preview.answer,
        ...(style.answerColor ? { color: style.answerColor } : {}),
      }}>
        Sous 2 à 4 semaines.
      </div>
    </div>
  );
};

/** La mise en forme du texte en premier, les réglages du volet ensuite. */
const TEXT_PANEL_ORDER = 0;

const AccordionPanelBox = ({ box }) => {
  const { t } = useTranslation();
  const { updateBox, isSelected, templates } = useStore(selector(box));
  const {
    props: style, setProp, labelOfColor, colorOfLabel,
  } = useBoxPropsEditor(box, 'panel');

  const styles = accordionPanelStyles(style);

  // Les barres de mise en forme suivent le dernier texte à avoir pris le focus.
  const [focusedKey, setFocusedKey] = useState('question');

  const applyTemplate = (template) => {
    updateBox(box.id, (draftBox) => {
      draftBox.content.panel = boxPropsFromStyle(template);
    });
  };

  return (
    // Largeur portée par la box elle-même, comme RichEditor et Title : le
    // Dragger (box non sélectionnée) n'en pose aucune sur son enveloppe, et
    // un `100%` s'y réduirait au contenu — seul le Resizer (box sélectionnée)
    // en fournit une.
    <div style={{ width: box.width, ...styles.frame }}>
      {/* Couleurs de texte : le DÉFAUT de la box. Un mot recoloré à la barre
          de mise en forme porte sa propre couleur et gagne. */}
      <div style={{
        padding: '12px 16px',
        ...styles.header,
        ...(style.questionColor ? { color: style.questionColor } : {}),
      }}>
        <BoxDraftText
          box={box}
          contentKey="question"
          isSelected={isSelected}
          isActive={focusedKey === 'question'}
          panelOrder={TEXT_PANEL_ORDER}
          placeholder={t('accordion_panel_question_placeholder')}
          style={{ fontWeight: 600 }}
          onFocusChange={setFocusedKey}
        />
      </div>
      <div style={{
        padding: '12px 16px',
        ...styles.answer,
        ...(style.answerColor ? { color: style.answerColor } : {}),
      }}>
        <BoxDraftText
          box={box}
          contentKey="answer"
          isSelected={isSelected}
          isActive={focusedKey === 'answer'}
          panelOrder={TEXT_PANEL_ORDER}
          placeholder={t('accordion_panel_answer_placeholder')}
          onFocusChange={setFocusedKey}
        />
      </div>

      {isSelected && (
        <>
          <BoxFormControlsPortal title={t('accordion_panel_template')} order={1}>
            <BoxStyleTemplatePicker
              styles={templates}
              props={style}
              onApply={applyTemplate}
              renderPreview={(template) => <PanelPreview style={template} />}
            />
          </BoxFormControlsPortal>
          <BoxFormControlsPortal title={t('accordion_panel')} order={2}>
            <AccordionPanelFields
              style={style}
              setProp={setProp}
              labelOfColor={labelOfColor}
              colorOfLabel={colorOfLabel}
            />
          </BoxFormControlsPortal>
          <BoxActionsPanel />
        </>
      )}
    </div>
  );
};

export default AccordionPanelBox;
